import { useRef } from "react"
import { CommonListToolbar } from "components/common/common-list-toolbar"
import { ListResultWrapper, MainComponent, ContentWrapper } from "components/common-styles"
import { rootStore } from "stores/root-store"
import { CommonListResult } from "components/common/common-list-result"
import { observer } from "mobx-react-lite"
import { add } from "date-fns"
import { getModuleName } from "utils/app-helper"
import { entityFromExportName, trackEvent } from "utils/analytics"

const CommonPage = ({
  DataStore,
  loadDataMethod,
  searchPlaceholder,
  filterData,
  afterLoadingData,
  filterStorageName,
  gridStorageName,
  allowArchivalSearch,
  columns,
  hiddenColumns,
  handleBulkDelete,
  searchRequiredWarningMsg,
  noDataMsg,
  exportFileName,
  idProp,
  columnsExtra,
  exportColumnsExtra,
  handleAddClicked,
  handleUploadClicked,
  handleRowClicked,
  onFilterItemValueChanged,
  aboveGrid,
  dataTransform,
  extraCustomFilters,
  renderGrid,
  skipAutoSearch,
  presetFilters,
  analyticsEntity,
}) => {
  const gridRef = useRef()

  const moduleName = getModuleName(filterStorageName)
  const userConfigs = rootStore.userConfigs ? rootStore.userConfigs[moduleName] : null
  const selectedUserConfig = !userConfigs ? null : !rootStore.selectedUserConfigId ? userConfigs[0] : userConfigs.find(x => x.Id == rootStore.selectedUserConfigId)

  const onSearch = async (currentFilterData, userInitiated = false) => {
    const customFilters = {}
    const dateItems = ["before_date", "after_date"]
    currentFilterData
      .filter(x => x.val && x.name != "after_date")
      .forEach(x => {
        let val = null
        if (dateItems.includes(x.name)) {
          let afterDate = new Date(),
            beforeDate = new Date()
          switch (x.val.mode) {
            case "TODAY":
              beforeDate = new Date()
              break
            case "YESTERDAY":
              beforeDate = add(new Date(), {
                days: -1,
              })
              break
            case "LAST_3_DAYS":
              beforeDate = add(new Date(), {
                days: -3,
              })
              break
            case "LAST_7_DAYS":
              beforeDate = add(new Date(), {
                days: -7,
              })
              break
            default:
              if (x.val.before_date) beforeDate = new Date(x.val.before_date)
              if (x.val.after_date) afterDate = new Date(x.val.after_date)
          }

          if (afterDate) {
            customFilters["after_date"] = afterDate
          }
          if (beforeDate) {
            customFilters["before_date"] = beforeDate
          }
        } else {
          val = x.val != "ALL" ? x.val : ""
          if (val) customFilters[x.name] = val
        }
      })

    if (!customFilters["search_term"]) customFilters["search_term"] = "%"
    if (extraCustomFilters) Object.assign(customFilters, extraCustomFilters)
    DataStore.setCustomFilters(customFilters)

    if (DataStore.setSearchText) DataStore.setSearchText(customFilters["search_term"])

    await DataStore[loadDataMethod || "loadData"]()

    if (afterLoadingData) afterLoadingData()

    if (userInitiated) {
      trackEvent("search", {
        entity: analyticsEntity || entityFromExportName(exportFileName) || moduleName.toLowerCase(),
        // filter names only, never what was typed or picked
        filters_used: Object.keys(customFilters)
          .filter(name => !(name === "search_term" && customFilters[name] === "%"))
          .join(","),
        results_count: DataStore.data?.length ?? 0,
      })
    }
    if (String(customFilters.order_status || "").includes("Exception") || customFilters.exception_type) trackEvent("exception_filter")
  }

  const onSaveSettings = async (name, currentFilterData, shouldCreateNewFilter) => {
    const currentHiddenColumns = gridRef.current.getCurrentHiddenColumns()
    const currentColumns = gridRef.current.getCurrentColumns()
    const req = {
      Config_Name: name,
      Config_Value: JSON.stringify({
        Module: moduleName,
        FilterData: currentFilterData,
        HiddenColumns: currentHiddenColumns,
        Columns: currentColumns,
      }),
    }
    const ok = await rootStore.addOrUpdateUserConfig(req, shouldCreateNewFilter)
    if (ok) await rootStore.loadUserConfigs()
  }

  const onDeleteCustomFilter = async id => {
    const ok = await rootStore.deleteUserConfig(id)
    if (ok) await rootStore.loadUserConfigs()
  }

  return (
    <MainComponent component="main">
      <ContentWrapper maxWidth={false}>
        <CommonListToolbar
          isLoading={DataStore.isLoading}
          filterData={filterData}
          searchPlaceholder={searchPlaceholder}
          filterStorageName={filterStorageName}
          allowArchivalSearch={allowArchivalSearch}
          onSaveSettings={onSaveSettings}
          onSearch={onSearch}
          company={rootStore.company}
          userConfigs={userConfigs}
          onDeleteCustomFilter={onDeleteCustomFilter}
          moduleName={moduleName}
          onFilterItemValueChanged={onFilterItemValueChanged}
          skipAutoSearch={skipAutoSearch}
          presetFilters={presetFilters}
        />
        {aboveGrid}
        <ListResultWrapper>
          {renderGrid ? (
            renderGrid({
              data: dataTransform ? dataTransform(DataStore.data) : DataStore.data,
              isLoading: DataStore.isLoading,
              searchText: DataStore.searchText,
            })
          ) : (
            <CommonListResult
              data={dataTransform ? dataTransform(DataStore.data) : DataStore.data}
              isLoading={DataStore.isLoading}
              searchText={DataStore.searchText}
              columns={columns}
              hiddenColumns={hiddenColumns}
              gridStorageName={gridStorageName}
              searchRequiredWarningMsg={searchRequiredWarningMsg}
              noDataMsg={noDataMsg}
              exportFileName={exportFileName}
              handleAddClicked={handleAddClicked}
              handleUploadClicked={handleUploadClicked}
              handleRowClicked={handleRowClicked}
              ref={gridRef}
              company={rootStore.company}
              userConfig={selectedUserConfig}
              moduleName={moduleName}
              idProp={idProp}
              columnsExtra={columnsExtra}
              exportColumnsExtra={exportColumnsExtra}
              handleBulkDelete={handleBulkDelete}
            />
          )}
        </ListResultWrapper>
      </ContentWrapper>
    </MainComponent>
  )
}

export default observer(CommonPage)
